import * as CL from '@design-system/component-library';
import { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType } from '../../common/enums.js';
import { t } from '../../common/i18n/index.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { ModalCloseAndSubmitButtons } from '../Modal/ModalCloseAndSubmitButtons.js';
import type { CommonError } from '../../common/types/errors.js';
import type { SupportCase } from '../../generated/api/models.js';
import type { JSX } from 'react';

import './SupportCaseDialog.scss';

export interface SupportCaseDialogProps {
  action: string;
  body: string | JSX.Element;
  featureName: SupportCase.FeatureEnum;
  featureItemId?: string;
  header: string;
  placeholder: string;
  isSubmitInProgress?: boolean;
  errors?: CommonError[];
  saveButtonLabel?: string;

  onCloseDialog: () => void;
  onSubmitDialog: (supportCase: SupportCase) => void;
}

export function getErrorsFromSupportCaseDialog(supportCase: SupportCase): CommonError[] | undefined {
  let errors: CommonError[] | undefined;
  if (!supportCase.description || supportCase.description.length < 1) {
    if (!errors) {
      errors = [];
    }
    errors.push({
      type: CommonErrorType.VALIDATION,
      field: 'description',
      message: t.JSP4('The message field must not be left empty.'),
    });
  }
  return errors;
}

interface SupportCaseFormValues {
  description: string;
}

const SupportCaseDialogForm = ({
  obj,
  placeholder,
  onCloseDialog,
  onSubmitDialog,
  isSubmitInProgress,
  saveButtonLabel,
}: {
  obj: SupportCase;
  placeholder: string;
  onCloseDialog: () => void;
  onSubmitDialog: (supportCase: SupportCase) => void;
  isSubmitInProgress?: boolean;
  saveButtonLabel?: string;
}) => {
  const methods = useForm<SupportCaseFormValues>({
    defaultValues: {
      description: '',
    },
    mode: 'onChange',
  });

  const { handleSubmit } = methods;

  const onSubmit = (values: SupportCaseFormValues) => {
    const supportCase: SupportCase = { ...obj, description: values.description };
    onSubmitDialog(supportCase);
    onCloseDialog();
  };

  return (
    <FormProvider {...methods}>
      <form className="of-support-case-dialog" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextAreaComponent
          name="description"
          placeholder={placeholder}
          required={true}
          label=""
          maxLength={29999}
          disabled={false}
        />
        <ModalCloseAndSubmitButtons
          submitButtonText={saveButtonLabel}
          onCancel={onCloseDialog}
          onSubmit={handleSubmit(onSubmit)}
          submitting={isSubmitInProgress}
        />
      </form>
    </FormProvider>
  );
};

export const SupportCaseDialog = ({
  action,
  featureName,
  featureItemId,
  header,
  body,
  placeholder,
  onCloseDialog,
  onSubmitDialog,
  isSubmitInProgress,
  errors,
  saveButtonLabel,
}: SupportCaseDialogProps) => {
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname, search } = useLocation();
  const absoluteUrl = `${siteBaseUrl}${pathname}${search}`;
  const generalErrors = errors && errors.filter(error => !error.field);
  const obj: SupportCase = {
    action,
    feature: featureName,
    featureItemId,
    url: absoluteUrl,
    description: '',
  };

  return (
    <DialogWrapper closeable header={header} onCloseDialog={onCloseDialog}>
      <div className={`${dsClass.MARGIN_TOP_4} support-case-dialog`}>
        {body}
        {generalErrors &&
          generalErrors.map((error, i) => (
            <CL.Disclaimer
              className={dsClass.TEXT_M}
              icon={<CL.Icon icon="warning" />}
              key={i}
              text={error.message}
              visible
            />
          ))}
      </div>
      <SupportCaseDialogForm
        obj={obj}
        placeholder={placeholder}
        onCloseDialog={onCloseDialog}
        onSubmitDialog={onSubmitDialog}
        isSubmitInProgress={isSubmitInProgress}
        saveButtonLabel={saveButtonLabel}
      />
    </DialogWrapper>
  );
};
